import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowDown } from "lucide-react";
import { Button, cn } from "@fox1ck-org/ui";
import { LogLine } from "./LogLine";
import { LogDetailPanel } from "./LogDetailPanel";
import { useFollowTail } from "./useFollowTail";
import type { LogRecord } from "./types";

interface LogsVirtualListProps {
  records: LogRecord[];
  searchTerm: string;
  showPod: boolean;
  defaultFollowing?: boolean;
  /** When true, the consumer is in live mode and the auto-scroll is enabled. */
  followEnabled?: boolean;
  onSelectPod?: (pod: string) => void;
  onSelectContainer?: (container: string) => void;
  onCorrelationClick?: (id: string) => void;
}

/**
 * Virtualized log list with follow-tail behavior. Renders thousands of
 * rows at 60fps via @tanstack/react-virtual. Each row is variable height
 * (long messages wrap), and expansion swaps in a LogDetailPanel.
 */
export function LogsVirtualList({
  records,
  searchTerm,
  showPod,
  defaultFollowing = true,
  followEnabled = true,
  onSelectPod,
  onSelectContainer,
  onCorrelationClick,
}: LogsVirtualListProps) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const follow = useFollowTail({
    threshold: 32,
    itemCount: records.length,
    defaultFollowing,
  });

  const virtualizer = useVirtualizer({
    count: records.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (i) => (records[i]?.id === expandedId ? 360 : 24),
    overscan: 12,
    measureElement:
      typeof window !== "undefined" && "ResizeObserver" in window
        ? (el) => el.getBoundingClientRect().height
        : undefined,
  });

  // Re-measure when expansion state changes (the row height jumps).
  useEffect(() => {
    virtualizer.measure();
  }, [expandedId, virtualizer]);

  const scrollToBottom = useMemo(
    () => () => {
      if (records.length === 0) return;
      virtualizer.scrollToIndex(records.length - 1, { align: "end" });
    },
    [virtualizer, records.length]
  );

  // Auto-scroll on new lines when following.
  useEffect(() => {
    if (followEnabled) follow.handleItemsAppended(scrollToBottom);
  }, [records.length, followEnabled, follow, scrollToBottom]);

  // Initial mount: jump to bottom if following.
  useEffect(() => {
    if (followEnabled && follow.isFollowing && records.length > 0) {
      scrollToBottom();
    }
    // intentionally only on first mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative h-full">
      <div
        ref={(el) => {
          parentRef.current = el;
          follow.registerScrollContainer(el);
        }}
        className="h-full overflow-auto bg-background/40"
      >
        {records.length === 0 ? null : (
          <div
            style={{
              height: virtualizer.getTotalSize(),
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const record = records[virtualRow.index]!;
              const isExpanded = record.id === expandedId;
              return (
                <div
                  key={record.id}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <LogLine
                    record={record}
                    expanded={isExpanded}
                    onToggleExpand={() =>
                      setExpandedId((curr) => (curr === record.id ? null : record.id))
                    }
                    onSelectPod={onSelectPod}
                    onSelectContainer={onSelectContainer}
                    searchTerm={searchTerm}
                    showPod={showPod}
                  />
                  {isExpanded && (
                    <LogDetailPanel
                      record={record}
                      onCorrelationClick={onCorrelationClick}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating "новые строки" pill while paused */}
      {followEnabled && !follow.isFollowing && follow.newSinceScroll > 0 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          <Button
            size="sm"
            className={cn(
              "shadow-lg gap-1.5 bg-primary/90 hover:bg-primary text-primary-foreground"
            )}
            onClick={() => follow.jumpToBottom(scrollToBottom)}
          >
            <ArrowDown className="h-3.5 w-3.5" />
            {follow.newSinceScroll} new line{follow.newSinceScroll === 1 ? "" : "s"}
          </Button>
        </div>
      )}
    </div>
  );
}
